import Hls from 'hls.js';

import { sendEvent } from '../event-store';

// How often a playing video is EXAMINED. Not how often it reports: since the
// thresholds below became the reason to send anything, a tick that crosses
// none of them posts nothing. The interval is the sampling rate of the check,
// and the check is arithmetic on numbers already in memory.
//
// It used to be the reporting rate, and that is what made it expensive.
// Measured on production over 90 days: 94 play-throughs produced 8 332
// `video_progress` rows, a median of 25 each and a maximum of 5 780 — one
// looping video in a tab nobody closed. 70% of those rows repeated a position
// of 100% and said nothing a previous row had not.
const HEARTBEAT_SECONDS = 10;

// Coverage thresholds, in percent of the film's length. Crossing one is the
// only thing that makes a playing video report, and each fires ONCE per
// play-through — a loop re-covers ground it has already covered and has
// nothing new to say.
//
// The measure behind them is COVERAGE and not elapsed playback: the share of
// the film's timeline that was actually on screen, counted as a set of
// distinct 5-second buckets. Watching the first fifth three times is 20%
// coverage, which is what the viewer saw; counting played seconds against the
// length would have called it 60%.
const COVERAGE_THRESHOLDS = [20, 40, 60, 80, 100];

// Slack allowed when enforcing "no seeking". Playback advances between the
// `seeking` event and our handler, and hls.js snaps to segment boundaries, so
// an exact comparison would fight normal playback.
const SEEK_TOLERANCE_SECONDS = 1.5;

// Granularity of the watched-timeline buckets, per Media Analytics guidance.
const SEEN_BUCKET_SECONDS = 5;

// Ceiling on how many buckets one play-through may accumulate — 10 hours of
// video. Keeps a stuck player from growing the beacon without bound.
const SEEN_BUCKET_LIMIT = 7200;

// Identifier of a single play-through. Media Analytics groups every request
// carrying the same value into one media record.
function newMediaId() {
  return 'm' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
}

// Verbose console log of every beacon, for checking instrumentation without a
// network tab. Enabled per browser with `localStorage.DEBUG_VIDEO = '1'` (same
// switch style as DEBUG_CONSENT in gtm.js) or per page load with
// `?debug_video=1`, which also persists it so a navigation keeps it on.
function isDebug() {
  try {
    if (window.location.search.indexOf('debug_video=1') !== -1) {
      window.localStorage.setItem('DEBUG_VIDEO', '1');
      return true;
    }
    if (window.location.search.indexOf('debug_video=0') !== -1) {
      window.localStorage.removeItem('DEBUG_VIDEO');
      return false;
    }
    return !!window.localStorage.getItem('DEBUG_VIDEO');
  } catch (error) {
    return false;
  }
}

// Send a playback beacon, logging it when debugging is on. Every event goes
// through here so the log can never drift from what was actually sent.
function track(name, params, video) {
  if (isDebug()) {
    console.log('[video] %s #%s', name, params.object_id || '?', params);
  }
  sendEvent(name, params, video);
}

// Attach a source to the element. hls.js (Media Source Extensions) is preferred
// wherever it runs — it exposes the renditions for the quality menu; only when
// it cannot do we fall back to native HLS (Safari/iOS). The order matters:
// some Chromium browsers answer `canPlayType('.m3u8')` with a hopeful "maybe"
// yet cannot really play it, so a native-first check would strand them.
function attachSource(video, source) {
  if (Hls.isSupported()) {
    const hls = new Hls({ startLevel: -1 });
    hls.loadSource(source);
    hls.attachMedia(video);
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        console.error('HLS fatal error', data.type, data.details);
        hls.destroy();
      }
    });
    // Expose the instance and signal once the renditions are known, so the UI
    // skin can build a real quality menu from hls.levels and drive switching.
    video.hlsInstance = hls;
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      video.dataset.hlsReady = '1';
      video.dispatchEvent(new CustomEvent('hls:ready'));
    });
    return;
  }

  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = source;
    return;
  }

  console.warn('HLS playback is not supported in this browser.');
}

// Wire playback reporting onto one media element. Events go through the shared
// store-event beacon, so they land as UserEvent rows and ride the same
// server-side dispatch as every other tracked event, rather than talking to
// any one tracker directly.
//
// The heartbeat carries three numbers because only together do they describe
// the viewing: position (where the playhead is), percent (the same against
// duration) and played (how much was really watched). A beacon reading
// position 55s / 34% / played 12s is a viewer who skipped ahead — no single
// number shows that.
function bindTracking(video) {
  const identity = {
    content_type_id: video.dataset.contentTypeId,
    object_id: video.dataset.objectId,
  };
  if (!identity.content_type_id || !identity.object_id) return;

  // Identifies one play-through end to end. Media Analytics folds every
  // request sharing it into a single record, so it must stay stable while the
  // same media plays and change when a new one starts.
  let mediaId = newMediaId();
  const readyAt = Date.now();

  let started = false;
  let playedSeconds = 0;
  let lastTimestamp = null;
  let heartbeat = null;
  let timeToPlay = null;
  let isSeeking = false;
  // Buckets of the timeline actually played, rounded down to 5 seconds. The
  // gaps are what the viewer skipped — this is what draws the audience curve,
  // and since it is a SET it is also what coverage is counted from.
  const seen = new Set();
  // Thresholds already reported for THIS play-through. Cleared with the
  // buckets, never on its own: the two answer the same question and a reset of
  // one without the other would either re-fire every threshold or silence them
  // for a play-through that has none.
  const fired = new Set();
  // Coverage carried by the last beacon that actually left, so a closing
  // report can tell „the viewer moved on" from „the same numbers again".
  let lastReportedCoverage = null;

  // Accumulate only forward movement of the playhead: a seek jumps
  // currentTime without anyone watching those seconds.
  video.addEventListener('timeupdate', () => {
    if (isSeeking) return;
    const bucketsBefore = seen.size;
    if (lastTimestamp !== null && video.currentTime > lastTimestamp) {
      const delta = video.currentTime - lastTimestamp;
      // A jump larger than a heartbeat is a seek, not viewing time.
      if (delta < HEARTBEAT_SECONDS) {
        playedSeconds += delta;
        if (seen.size < SEEN_BUCKET_LIMIT) {
          seen.add(Math.floor(video.currentTime / SEEN_BUCKET_SECONDS) * SEEN_BUCKET_SECONDS);
        }
      }
    }
    lastTimestamp = video.currentTime;

    // Coverage can only have moved if a NEW bucket was added, and a bucket is
    // five seconds wide — so this costs one integer comparison per
    // `timeupdate` and reaches the threshold check at most once every five
    // seconds of playback.
    //
    // Checked here rather than on the timer because a threshold has to be
    // reported when it is crossed, not at the next sample: somebody who
    // reaches 20% and closes the tab two seconds later would otherwise never
    // join the audience they earned, and the closing report cannot stand in
    // for them — it carries no threshold by design.
    if (started && seen.size !== bucketsBefore) reportProgress();
  });

  // The media half of every beacon — identical whichever moment triggered it,
  // so a pause, a heartbeat and a finish all describe the same play-through.
  function mediaPayload() {
    const payload = {
      media_id: mediaId,
      played_seconds: Math.round(playedSeconds),
      position_seconds: Math.round(video.currentTime),
      is_fullscreen: document.fullscreenElement === video ? '1' : '0',
    };
    if (timeToPlay !== null) payload.time_to_play = timeToPlay;
    if (seen.size) {
      payload.seen_positions = Array.from(seen).sort((a, b) => a - b).join(',');
    }
    const coverage = coveragePercent();
    if (coverage !== null) payload.coverage_percent = coverage;
    if (video.duration && isFinite(video.duration)) {
      payload.duration_seconds = Math.round(video.duration);
    }
    // Intrinsic resolution of what is actually playing. With adaptive
    // streaming this changes mid-playback as hls.js switches rendition, which
    // is exactly what the `resize` event reports.
    if (video.videoWidth && video.videoHeight) {
      payload.video_width = video.videoWidth;
      payload.video_height = video.videoHeight;
    }
    return payload;
  }

  // Share of the film's timeline that has actually been on screen, as whole
  // percent, or null while the duration is unknown.
  //
  // Counted from the bucket SET, so replaying a stretch adds nothing — which
  // is the whole difference between this and played time. Capped at 100
  // because a loop keeps adding buckets it already holds only in the sense
  // that it does not: the set cannot exceed the film, but rounding can, and a
  // threshold list ending at 100 must be reachable exactly.
  function coveragePercent() {
    if (!video.duration || !isFinite(video.duration) || video.duration <= 0) return null;
    const covered = seen.size * SEEN_BUCKET_SECONDS;
    return Math.min(100, Math.round((covered / video.duration) * 100));
  }

  // Thresholds crossed since the last check, in order, each returned once.
  function newlyCrossed() {
    const coverage = coveragePercent();
    if (coverage === null) return [];
    const crossed = COVERAGE_THRESHOLDS.filter(
      (threshold) => coverage >= threshold && !fired.has(threshold));
    crossed.forEach((threshold) => fired.add(threshold));
    return crossed;
  }

  // Report the play-through.
  //
  // `closing` marks the moments that end a stretch of watching — a pause, a
  // seek, the tab going away — where the reading has to leave even if no
  // threshold was crossed. Without it a viewer who watches 15% and leaves
  // would be recorded by Matomo with the watch time of the opening beacon,
  // which is none: the media record is refined by every request and closed by
  // the last one, so somebody who never reaches a threshold needs that last
  // one to exist.
  //
  // Everything else is silence by design. A tick that crosses nothing repeats
  // the previous reading, and Matomo folds it into the same record for no
  // gain, Meta counts nothing, GA4 counts nothing, and we store a row.
  function reportProgress(closing) {
    if (!video.duration || !isFinite(video.duration)) return;

    const crossed = newlyCrossed();
    const coverage = coveragePercent();

    crossed.forEach((threshold) => {
      track('video_progress', {
        ...identity,
        ...mediaPayload(),
        percent: Math.round((video.currentTime / video.duration) * 100),
        threshold,
      }, video);
      lastReportedCoverage = coverage;
    });

    if (crossed.length || !closing) return;
    // Nothing crossed and this is a closing moment: send only if the reading
    // moved since whatever went out last, so a pause followed by a pagehide
    // does not post the same numbers twice.
    if (lastReportedCoverage !== null && coverage <= lastReportedCoverage) return;
    track('video_progress', {
      ...identity,
      ...mediaPayload(),
      percent: Math.round((video.currentTime / video.duration) * 100),
    }, video);
    lastReportedCoverage = coverage;
  }

  function stopHeartbeat() {
    if (heartbeat !== null) {
      window.clearInterval(heartbeat);
      heartbeat = null;
    }
  }

  // Muted autoplay is a silent preview, not a view: no beacon fires until the
  // viewer turns the sound on. `armed` stays false for an autoplay video until
  // `video:engage` (dispatched by the autoplay overlay); a normal, user-started
  // video is armed from the outset.
  let armed = !video.hasAttribute('autoplay');

  function startView() {
    if (started) return;
    started = true;
    timeToPlay = Math.round((Date.now() - readyAt) / 1000);
    track('video_start', { ...identity, ...mediaPayload() }, video);
  }

  video.addEventListener('play', () => {
    lastTimestamp = video.currentTime;
    // Muted autoplay preview — hold every beacon back until engage.
    if (!armed) return;
    // Only the first play opens a view; later ones are resumes after a pause.
    startView();
    // Heartbeats run only while playing — a paused tab must not keep
    // reporting a position nobody is watching.
    stopHeartbeat();
    heartbeat = window.setInterval(reportProgress, HEARTBEAT_SECONDS * 1000);
  });

  // Sound turned on (overlay click or volume control). The muted preview is not
  // part of the view — discard its played time and start the view here, keeping
  // the current position.
  video.addEventListener('video:engage', () => {
    if (armed) return;
    armed = true;
    playedSeconds = 0;
    seen.clear();
    fired.clear();
    lastReportedCoverage = null;
    lastTimestamp = video.currentTime;
    if (!video.paused) {
      startView();
      stopHeartbeat();
      heartbeat = window.setInterval(reportProgress, HEARTBEAT_SECONDS * 1000);
    }
  });

  // Pause and fullscreen both change what the media record should say, and
  // neither waits for the next heartbeat — report them as they happen.
  video.addEventListener('pause', () => {
    stopHeartbeat();
    if (started && !video.ended) reportProgress(true);
  });

  document.addEventListener('fullscreenchange', () => {
    if (started) reportProgress();
  });

  // Seeking must not be counted as viewing. `timeupdate` already refuses to
  // credit a jump, but the pair of handlers makes the intent explicit and
  // stops the bucket set from gaining the position landed on.
  video.addEventListener('seeking', () => {
    isSeeking = true;
  });

  video.addEventListener('seeked', () => {
    isSeeking = false;
    // Start counting afresh from where the viewer landed, so the skipped
    // stretch never enters played time.
    lastTimestamp = video.currentTime;
    if (started) reportProgress(true);
  });

  // Fires when the intrinsic dimensions change — an adaptive-streaming
  // rendition switch. Worth reporting: it is what the viewer actually saw.
  video.addEventListener('resize', () => {
    if (started) reportProgress();
  });

  video.addEventListener('ended', () => {
    stopHeartbeat();
    // A muted preview that reaches the end is not a completed view.
    if (started) track('video_complete', { ...identity, ...mediaPayload() }, video);
    // A replay is a new play-through: fresh id, counters back to zero.
    mediaId = newMediaId();
    started = false;
    playedSeconds = 0;
    lastTimestamp = null;
    timeToPlay = null;
    seen.clear();
    fired.clear();
    lastReportedCoverage = null;
  });

  // A viewer who closes the tab mid-play would otherwise leave the last
  // heartbeat window unreported.
  window.addEventListener('pagehide', () => {
    if (started && !video.paused) reportProgress(true);
  });
}

// Enforce the playback restrictions the markup asked for.
//
// Caveat worth stating plainly: this discourages, it does not prevent. Both
// guards live in the page's own JS, so anyone with devtools can step around
// them. They are for keeping an ordinary viewer on the intended path, not for
// content protection.
function bindRestrictions(video) {
  const allowPause = video.dataset.videoAllowPause !== '0';
  const allowSeek = video.dataset.videoAllowSeek !== '0';

  if (!allowPause) {
    video.addEventListener('pause', () => {
      // `ended` fires a pause too; resuming there would loop the video.
      if (!video.ended) video.play().catch(() => {});
    });
  }

  if (!allowSeek) {
    let furthest = 0;
    video.addEventListener('timeupdate', () => {
      if (!video.seeking && video.currentTime > furthest) furthest = video.currentTime;
    });
    video.addEventListener('seeking', () => {
      if (video.currentTime > furthest + SEEK_TOLERANCE_SECONDS) {
        video.currentTime = furthest;
      }
    });
  }
}

// Export
export function init() {
  document.querySelectorAll('video[data-video-hls]').forEach((video) => {
    if (video.dataset.videoBound) return;
    video.dataset.videoBound = '1';

    attachSource(video, video.dataset.videoHls);
    bindTracking(video);
    bindRestrictions(video);
  });
}
